/**
 * The housekeeping half of the property-management back office (C9 §5.5).
 *
 * `src/router/index.ts` is shared by every module and belongs to the wiring
 * packet, so this file declares the records and the router spreads
 * `HOUSEKEEPING_ROUTES` in beside `PMS_ROUTES`. They are declared here rather
 * than in `@/modules/pms/routes` for the reason that file states in its own
 * header: the commercial and operations halves are built by different packets,
 * and folding both into one table would make whichever landed second the owner
 * of the other's paths.
 *
 * The permission on each route follows the API rather than the topic. The
 * room-status grid is `pms.housekeeping.read` because that is what
 * `GET /pms-housekeeping-tasks` declares — *setting* a status needs
 * `pms.housekeeping.status_set` and *inspecting* needs
 * `pms.housekeeping.inspect`, and the grid checks each separately, because they
 * are a segregation-of-duties pair and a screen that inferred one from the other
 * would be the hole the pair exists to close.
 */

import type { RouteRecordRaw } from 'vue-router'

import {
  HOUSEKEEPING_EXTERNAL_PERMISSIONS,
  HOUSEKEEPING_PERMISSIONS,
} from '@/modules/pms/housekeeping/permissions'

export const HOUSEKEEPING_ROUTES: RouteRecordRaw[] = [
  {
    path: '/pms/housekeeping/boards',
    name: 'pms-housekeeping-boards',
    component: () => import('@/modules/pms/housekeeping/HousekeepingBoardsView.vue'),
    meta: {
      titleKey: 'pms.housekeeping.routeTitles.boards',
      permission: HOUSEKEEPING_PERMISSIONS.read,
    },
  },
  {
    path: '/pms/housekeeping/rooms',
    name: 'pms-housekeeping-rooms',
    component: () => import('@/modules/pms/housekeeping/RoomStatusGridView.vue'),
    meta: {
      titleKey: 'pms.housekeeping.routeTitles.rooms',
      permission: HOUSEKEEPING_PERMISSIONS.read,
    },
  },
  {
    path: '/pms/housekeeping/faults',
    name: 'pms-housekeeping-faults',
    component: () => import('@/modules/pms/housekeeping/FaultQueueView.vue'),
    meta: {
      titleKey: 'pms.housekeeping.routeTitles.faults',
      permission: HOUSEKEEPING_PERMISSIONS.read,
    },
  },
  {
    path: '/pms/housekeeping/out-of-order',
    name: 'pms-housekeeping-out-of-order',
    component: () => import('@/modules/pms/housekeeping/OutOfOrderCalendarView.vue'),
    meta: {
      titleKey: 'pms.housekeeping.routeTitles.outOfOrder',
      // The calendar reads and writes `pms_room_out_of_order`, a masters table,
      // and the masters family is what guards it. Guarding it with
      // `housekeeping.read` would let a reader open a screen whose every action
      // the server then refuses.
      permission: HOUSEKEEPING_EXTERNAL_PERMISSIONS.roomOutOfOrder,
    },
  },
]

/** The same set as strings, for links inside the module. */
export const HOUSEKEEPING_PATHS = {
  boards: '/pms/housekeeping/boards',
  rooms: '/pms/housekeeping/rooms',
  faults: '/pms/housekeeping/faults',
  outOfOrder: '/pms/housekeeping/out-of-order',
} as const
